import React, { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, DialogOverlay, DialogOverlayActions, DialogOverlayFooter, InlineButton } from '@fs/zion-ui'
import { FormAutosuggest, useZionFormContext } from '@fs/zion-form'
import { useSlsStateContext } from '@fs/zion-sls'
import { getPersonsFromIds } from '../../../../../components/generic/panels/editHouseholdPanel/utils/EditHouseholdSaveUtils'
import { autosuggestPrioritySort, getRecordTypesForAutosuggest } from '../../../../../../shared/utils/utils'

const NewRecordDialogOverlay = ({ newRecordOverlay, savePersonInOtherRecord }) => {
  const [t] = useTranslation()
  const zionSlsState = useSlsStateContext()
  const { types, images, currentImageArk } = zionSlsState
  const formMethods = useZionFormContext()
  const selectedPersonsIds = formMethods.getValues('selectedPersonsIds')

  const recordTypeOptions = useMemo(
    () => getRecordTypesForAutosuggest(types, images, currentImageArk, t),
    [t, types, currentImageArk, images]
  )

  const handleSave = useCallback(async () => {
    const allPersonsToMove = getPersonsFromIds({ images, personsIds: selectedPersonsIds })
    const newRecordType = formMethods.getValues('newRecordType') || ''

    // Save all persons to the destination record
    savePersonInOtherRecord('new_record', allPersonsToMove, zionSlsState.currentImageArk, newRecordType)

    formMethods.setValue('selectedPersons', [])
    newRecordOverlay.close()
  }, [savePersonInOtherRecord, selectedPersonsIds, formMethods, zionSlsState, newRecordOverlay, images])

  return (
    <DialogOverlay {...newRecordOverlay} title={t('new-record-dialog.title', 'New Record')}>
      {t('new-record-dialog.content', 'What is the record type? If you are unsure of what to enter, leave it blank.')}
      <FormAutosuggest
        label={t('new-record-dialog.record-types', 'Record Types')}
        placeholder={t('new-record-dialog.record-types', 'Record Types')}
        suggestions={recordTypeOptions}
        name="newRecordType"
        helpMessage={
          <>
            {t('new-record-dialog.help-message', 'What are record types?')}
            &nbsp;
            <InlineButton to="https://www.familysearch.org/help/helpcenter/article/what-is-a-record-type">
              {t('new-record-dialog.learn-more', 'Learn More')}
            </InlineButton>
          </>
        }
        sortFunction={autosuggestPrioritySort}
        clearOnClickAway
      />

      <DialogOverlayFooter>
        <DialogOverlayActions>
          <Button data-testid="undo-new-record-dialog" onClick={() => newRecordOverlay.close()}>
            {t('new-record-dialog.undo.btn', 'Undo')}
          </Button>
          <Button data-testid="save-new-record-dialog" onClick={handleSave} emphasis="high">
            {t('new-record-dialog.save.btn', 'Save')}
          </Button>
        </DialogOverlayActions>
      </DialogOverlayFooter>
    </DialogOverlay>
  )
}

export default NewRecordDialogOverlay
